import React, { useState, useEffect } from 'react';
import { Ingredient } from '../../../types/recipe';
import { parseFractionToDecimal } from '../../../utils/fractionParser';

const UNITS = ['', 'cup', 'tbsp', 'tsp', 'oz', 'lb', 'g', 'kg', 'ml', 'L', 'piece', 'can', 'package'];

interface IngredientRowProps {
  ingredient: Ingredient;
  components: { id: string; name: string }[];
  onChange: (ingredient: Ingredient) => void;
  onRemove: () => void;
  isComponent?: boolean;
}

export const IngredientRow: React.FC<IngredientRowProps> = ({
  ingredient,
  components,
  onChange,
  onRemove,
  isComponent = false
}) => {
  const [localIngredient, setLocalIngredient] = useState(ingredient);

  useEffect(() => {
    setLocalIngredient(ingredient);
  }, [ingredient]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updated = { ...localIngredient, name: e.target.value };
    setLocalIngredient(updated);
    onChange(updated);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const amountDisplay = e.target.value;
    const amountNumeric = parseFractionToDecimal(amountDisplay);
    const updated = { ...localIngredient, amountDisplay, amount: amountNumeric };
    setLocalIngredient(updated);
    onChange(updated);
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const updated = { ...localIngredient, unit: e.target.value };
    setLocalIngredient(updated);
    onChange(updated);
  };

  const handleComponentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedComponent = components.find(c => c.id === e.target.value);
    if (selectedComponent) {
      const updated = {
        ...localIngredient,
        componentId: selectedComponent.id,
        name: selectedComponent.name,
        unit: 'units'
      };
      setLocalIngredient(updated);
      onChange(updated);
    }
  };

  return (
    <div className="ingredient-row" style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
      {isComponent ? (
        <select
          className="component-select"
          value={localIngredient.componentId || ''}
          onChange={handleComponentChange}
          style={{ flex: 2 }}
        >
          <option value="">Select component...</option>
          {components.map(comp => (
            <option key={comp.id} value={comp.id}>
              {comp.name}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          className="ingredient-input"
          placeholder="Enter ingredient"
          value={localIngredient.name}
          onChange={handleNameChange}
          style={{ flex: 2 }}
        />
      )}

      <input
        type="text"
        className="amount-input"
        placeholder="Amount"
        value={localIngredient.amountDisplay}
        onChange={handleAmountChange}
        title="Enter decimal (0.5) or fraction (1/2, 1 1/2)"
        style={{ flex: 1 }}
      />

      {isComponent ? (
        <span style={{ flex: 1, display: 'flex', alignItems: 'center' }}>component</span>
      ) : (
        <select className="unit-select" value={localIngredient.unit} onChange={handleUnitChange} style={{ flex: 1 }}>
          {UNITS.map(unit => (
            <option key={unit || 'unit'} value={unit}>
              {unit || 'unit'}
            </option>
          ))}
        </select>
      )}

      <button
        type="button"
        className="remove-ingredient-btn"
        onClick={onRemove}
        style={{ width: '32px', cursor: 'pointer' }}
      >
        ×
      </button>
    </div>
  );
};
